'use client';

import { Typography } from '@mui/material';
import type { SparkJob } from '@repo/api-gw-sdk';
import type { ColumnDef } from '@tanstack/react-table';

import { dayjs } from '@/utils/dayjs';

import { SparkJobStatusCell } from './sparkJobStatusCell';

export function getSparkUIUrl(
  projectId: string,
  workspaceId: string,
  job: SparkJob
): string | null {
  const prefix = `/projects/${projectId}/lakehouse/workspace/${workspaceId}`;
  if (job.status === 'RUNNING') {
    return `${prefix}/spark-ui/live/engines/${job.sparkEngineId}/jobs/${job.id}/`;
  }
  if (job.status === 'SUCCESS' || job.status === 'FAILED') {
    if (!job.sparkApplicationId) return null;
    return `${prefix}/spark-history/history/${job.sparkApplicationId}/jobs/`;
  }
  return null;
}

export function createSparkJobsTableColumns(
  workspaceId: string,
  projectId: string,
  engineNames?: Record<string, string>
): ColumnDef<SparkJob>[] {
  return [
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      size: 80,
      minSize: 80,
      maxSize: 80,
      enableSorting: false,
      cell: ({ row }) => <SparkJobStatusCell status={row.original.status} />,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      size: 250,
      cell: ({ row, table }) => {
        const togglePanel = () => {
          if (row.getIsSelected()) {
            table.resetRowSelection(true);
          } else {
            table.setRowSelection({ [row.id]: true });
          }
        };

        return (
          <Typography
            noWrap
            title={row.original.name}
            className='underline hover:cursor-pointer hover:text-(--mui-palette-text-secondary)'
            onClick={togglePanel}
          >
            {row.original.name}
          </Typography>
        );
      },
    },
    {
      id: 'sparkEngineId',
      accessorKey: 'sparkEngineId',
      header: 'Spark Engine',
      size: 200,
      cell: ({ row }) => {
        const engineId = row.original.sparkEngineId;
        const name = engineNames?.[engineId];
        return (
          <Typography noWrap title={engineId}>
            {name || engineId}
          </Typography>
        );
      },
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: 'Created',
      size: 180,
      cell: ({ row }) => (
        <Typography variant='body2'>
          {dayjs.utc(row.original.createdAt).format('MMM D, YYYY HH:mm:ss')}
        </Typography>
      ),
    },
    {
      id: 'sparkUi',
      header: 'Spark UI',
      size: 100,
      minSize: 100,
      maxSize: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const url = getSparkUIUrl(projectId, workspaceId, row.original);
        if (!url) return null;

        return (
          <Typography
            variant='body2'
            className='hover:cursor-pointer'
            sx={{ color: 'var(--mui-palette-primary-main)' }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            ↗
          </Typography>
        );
      },
    },
  ];
}
